import React, { useEffect, useMemo, useState, useRef } from "react";
import "./Travelers.css";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";


function CountUp({ to = 0, duration = 900, prefix = "", suffix = "" }) {
  const [val, setVal] = useState(0);
  const startTs = useRef(null);
  const target = Number(to || 0);

  useEffect(() => {
    startTs.current = null;
    let raf = 0;

    const step = (ts) => {
      if (!startTs.current) startTs.current = ts;
      const p = Math.min(1, (ts - startTs.current) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  const formatted = Math.round(val).toLocaleString();
  return <>{prefix}{formatted}{suffix}</>;
}

// Status component
export function Status({ status, small = false, onClick }) {
  const clsBase = "badge";
  const clsColor = status === "Active" ? "green" : "red";
  const cls = `${clsBase} ${clsColor}`;
  const style = small
    ? { padding: "4px 8px", fontSize: 12, borderRadius: 12, display: "inline-block", cursor: onClick ? "pointer" : "default", border: "none" }
    : { padding: "6px 10px", borderRadius: 14, display: "inline-block", cursor: onClick ? "pointer" : "default", border: "none" };
  
  if (onClick) {
    return (
      <button className={`${cls} as-button`} style={{ ...style }} onClick={onClick}>
        {status}
      </button>
    );
  }
  return (
    <span className={cls} style={{ ...style }}>
      {status}
    </span>
  );
}

export default function TravelersAll() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalTravelers: 0, activeTravelers: 0, inactiveTravelers: 0 });
  const [allRows, setAllRows] = useState([]); // Store all data
  const [rows, setRows] = useState([]); // Filtered data for display
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ search: "", status: "", dateFrom: "", dateTo: "" });

  // Cards for the dashboard
  const cards = useMemo(
    () => [
      { title: "Total Travelers", value: stats.totalTravelers, sub: "Total Travelers", icon: "🙍‍♂️", prefix: "+" },
      { title: "Active Travelers", value: stats.activeTravelers, sub: "Active Travelers", icon: "🟢", prefix: "+" },
      { title: "Inactive Travelers", value: stats.inactiveTravelers, sub: "Inactive Travelers", icon: "🔴", prefix: "+" },
    ],
    [stats]
  );

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const token = localStorage.getItem("adminToken");
        const headers = {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const [summaryRes, listRes] = await Promise.all([
          fetch(`${API}/admin/travelers/all-summary`, { headers }),
          fetch(`${API}/admin/travelers/all-travelers`, { headers }), // fetch ALL travelers, no filters
        ]);

        const summary = await summaryRes.json();
        const list = await listRes.json();

        if (!mounted) return;

        setStats({
          totalTravelers: Number(summary.totalTravelers) || 0,
          activeTravelers: Number(summary.activeTravelers) || 0,
          inactiveTravelers: Number(summary.inactiveTravelers) || 0,
        });

        const processedRows = (Array.isArray(list) ? list : []).map(r => ({
          ...r,
          dateText: r.date ? new Date(r.date).toLocaleString() : "—",
          status: r.status || "Inactive",
        }));

        setAllRows(processedRows);
        setRows(processedRows); // initially show all
      } catch (e) {
        if (mounted) setError(e.message || "Load failed");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => { mounted = false; };
  }, []); // empty deps → fetch only once

  async function toggleStatus(userId) {
    try {
      // optimistic UI
      setAllRows((prev) =>
        prev.map((r) =>
          r.userId === userId ? { ...r, status: r.status === "Active" ? "Inactive" : "Active" } : r
        )
      );

      const token = localStorage.getItem("adminToken");
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      const res = await fetch(`${API}/admin/travelers/all-travelers/${userId}/status`, {
        method: "PATCH",
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to update status.");

      // precise update + refresh cards
      setAllRows((prev) =>
        prev.map((r) =>
          r.userId === userId
            ? {
              ...r,
              status: data.row.status,
              fullName: data.row.fullName,
              phoneNumber: data.row.phoneNumber,
              email: data.row.email,
              date: data.row.date,
              dateText: data.row.date ? new Date(data.row.date).toLocaleString() : "—",
            }
            : r
        )
      );
      setStats({
        totalTravelers: Number(data.summary.totalTravelers) || 0,
        activeTravelers: Number(data.summary.activeTravelers) || 0,
        inactiveTravelers: Number(data.summary.inactiveTravelers) || 0,
      });
    } catch (e) {
      console.error(e);
      alert(e.message || "Failed to update status.");
      // Optional: reload list for consistency
      try {
        const token = localStorage.getItem("adminToken");
        const headers = {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        };
        const listRes = await fetch(`${API}/admin/travelers/all-travelers`, { headers });
        const list = await listRes.json();
        if (listRes.ok) {
          setAllRows(
            (Array.isArray(list) ? list : []).map((r) => ({
              ...r,
              dateText: r.date ? new Date(r.date).toLocaleString() : "—",
              status: r.status || "Inactive",
            }))
          );
        }
      } catch { }
    }
  }

  useEffect(() => {
    let filtered = [...allRows];

    // Filter by status
    if (filters.status) {
      filtered = filtered.filter(row => row.status === filters.status);
    }

    //Filter by date
    if (filters.dateFrom) {
      const from = new Date(filters.dateFrom);
      filtered = filtered.filter(row => new Date(row.date) >= from);
    }
    if (filters.dateTo) {
      const to = new Date(filters.dateTo);
      filtered = filtered.filter(row => new Date(row.date) <= to);
    }

    //  Filter by search
    if (filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(row =>
        row.fullName?.toLowerCase().includes(searchTerm) ||
        row.email?.toLowerCase().includes(searchTerm) ||
        row.phoneNumber?.toLowerCase().includes(searchTerm)
      );
    }

    setRows(filtered);
  }, [filters, allRows]);


  // Client-side search filtering
  useEffect(() => {
    if (!filters.search.trim()) {
      setRows(allRows); // Show all rows if no search
    } else {
      const searchTerm = filters.search.toLowerCase();
      const filteredRows = allRows.filter(row =>
        row.fullName?.toLowerCase().includes(searchTerm) ||
        row.email?.toLowerCase().includes(searchTerm)
      );
      setRows(filteredRows);
    }
  }, [filters.search, allRows]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => setFilters({ search: "", status: "", dateFrom: "", dateTo: "" });

  if (error) return <div className="owners-page"><p style={{ color: "red" }}>{error}</p></div>;

  return (
    <div className="owners-page">
      <div className="owners-head">
        <h2 className="owners-title">Travelers</h2>
        <p className="muted">Manage all Travelers</p>
      </div>

      <section className="owners-cards">
        {cards.map((s, i) => (
          <div key={i} className="card stat">
            <div className="stat-title">{s.title}</div>
            <div className="stat-value"><CountUp to={s.value} prefix={s.prefix} /></div>
            <div className="stat-sub">{s.sub}</div>
            <div className="lock" aria-hidden>{s.icon}</div>
          </div>
        ))}
      </section>

      <div className="filters">
        <input type="text" name="search" value={filters.search} onChange={handleFilterChange} placeholder="Search by Name or Email" />
        <select name="status" value={filters.status} onChange={handleFilterChange}>
          <option value="">All Statuses</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
        <input type="date" name="dateFrom" value={filters.dateFrom} onChange={handleFilterChange} />
        <input type="date" name="dateTo" value={filters.dateTo} onChange={handleFilterChange} />
        <button onClick={handleClearFilters}>Clear All</button>
      </div>

      <h3 className="owners-subtitle">All Travelers</h3>
      <section className={`card table-card ${loading ? 'loading' : ''}`}>
        <table className="table">
          <thead>
            <tr>
              <th>Full Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Registered</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.userId}>
                <td>{r.fullName}</td>
                <td>{r.email}</td>
                <td>{r.phoneNumber}</td>
                <td>{r.dateText}</td>
                <td><Status status={r.status} onClick={() => toggleStatus(r.userId)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
